/* eslint-disable @typescript-eslint/no-explicit-any, react-hooks/exhaustive-deps, @typescript-eslint/no-unused-vars */
import React, { useMemo, useState, useLayoutEffect, useRef } from 'react';
import { DataTable } from 'motili-ui';
import { Tooltip } from 'react-tooltip';
import { BaseUrl, TableHeaders } from '../../models/WidgetModels';
import { snakeCaseToTitleCase } from '../../../utils/WidgetUtils';
import { useGetFilteredWorkOrderExceptionsQuery } from 'widgets/store/api/ExceptionFlagWidgetApi';
import { buildFlagIndex } from 'widgets/IndividualWidgets/ExceptionFlag/ExceptionFlagIndex';
import { ShowingInline, PaginationInline } from 'widgets/components/paging/Paging';
import {
    EXCEPTION_FLAG,
    ExceptionFlagFilters,
    TableRecords,
    WorkOrderExceptionCountAPIResult,
} from 'widgets/models/ExceptionFlagModel';

const MIN_TABLE_WIDTH = 1200;

const raf = () => new Promise<void>(r => requestAnimationFrame(() => r()));
async function waitForStable(el: HTMLElement) {
    for (let i = 0; i < 40; i++) {
        const w1 = el.clientWidth;
        const o1 = getComputedStyle(el).opacity;
        await raf(); await raf();
        const w2 = el.clientWidth;
        const hasHead = !!el.querySelector('thead th');
        const hasRow  = !!el.querySelector('tbody tr');
        if (o1 === '1' && w1 === w2 && w2 > 0 && hasHead && hasRow) return;
    }
}

const setImp = (s: CSSStyleDeclaration, k: string, v: string) =>
    s.setProperty(k, v, 'important');

function normalizeTbody(root: HTMLElement) {
    root.querySelectorAll('table.motili-data-table > tbody').forEach((tb) => {
        const el = tb as HTMLElement;
        el.classList.remove('iyxFJp');
        setImp(el.style, 'float', 'none');
        setImp(el.style, 'display', 'table-row-group');
        setImp(el.style, 'line-height', 'normal');
        setImp(el.style, 'font-style', 'normal');
        setImp(el.style, 'clear', 'none');
        setImp(el.style, 'position', 'static');
        setImp(el.style, 'width', 'auto');
    });
}

function relaxFlexAncestors(node: HTMLElement) {
    let el: HTMLElement | null = node;
    for (let i = 0; i < 6 && el; i++) {
        const p = el.parentElement as HTMLElement | null;
        if (!p) break;
        const cs = getComputedStyle(p);
        if (cs.display === 'flex' || cs.display === 'inline-flex') {
            setImp(p.style, 'min-width', '0');
            setImp(p.style, 'overflow', 'visible');
        }
        el = p;
    }
}

function forceHorizontalOnlyScroll(root: HTMLElement) {
    const scroller = root.closest('.ef-table-scroll') as HTMLElement | null;
    const table = root.querySelector('table') as HTMLTableElement | null;
    if (!scroller || !table) return;
    setImp(scroller.style, 'min-width', '0');
    setImp(scroller.style, 'width', '100%');
    setImp(scroller.style, 'overflow-x', 'auto');
    setImp(scroller.style, 'overflow-y', 'hidden');
    setImp(scroller.style, 'max-height', 'none');
    setImp(scroller.style, '-webkit-overflow-scrolling', 'touch');
    setImp(scroller.style, 'scrollbar-gutter', 'stable');
    setImp(scroller.style, 'background', '#fff');
    setImp(scroller.style, 'border-radius', '0');

    relaxFlexAncestors(scroller);

    // ----- TABLE CORE -----
    table.classList.remove('table', 'table-hover');
    table.classList.add('motili-data-table');
    setImp(table.style, 'table-layout', 'fixed');
    setImp(table.style, 'border-collapse', 'collapse');
    setImp(table.style, 'border-spacing', '0');
    setImp(table.style, 'width', 'auto');
    setImp(table.style, 'min-width', `${MIN_TABLE_WIDTH}px`);

    table.querySelectorAll<HTMLTableCellElement>('thead th').forEach(th => {
        setImp(th.style, 'background', '#0E2248');
        setImp(th.style, 'color', '#FFFFFF');
        setImp(th.style, 'vertical-align', 'middle');
        setImp(th.style, 'padding', '8px 12px');
        setImp(th.style, 'line-height', '1.3');
        setImp(th.style, 'box-sizing', 'border-box');
    });

    // body cells: nowrap + ellipsis
    table.querySelectorAll<HTMLTableCellElement>('th, td').forEach(cell => {
        setImp(cell.style, 'padding', '8px 12px');
        setImp(cell.style, 'line-height', '1.4');
        setImp(cell.style, 'box-sizing', 'border-box');
        setImp(cell.style, 'white-space', 'nowrap');
        setImp(cell.style, 'overflow', 'hidden');
        setImp(cell.style, 'text-overflow', 'ellipsis');
    });

    table.querySelectorAll<HTMLTableCellElement>('tbody td').forEach(td => {
        setImp(td.style, 'vertical-align', 'top');
        Array.from(td.children).forEach((child) => {
            const el = child as HTMLElement;
            setImp(el.style, 'display', 'inline-flex');
            setImp(el.style, 'align-items', 'center');
            setImp(el.style, 'gap', '6px');
            setImp(el.style, 'margin', '0');
            setImp(el.style, 'padding', '0');
            setImp(el.style, 'max-width', '100%');
            setImp(el.style, 'white-space', 'inherit');
            setImp(el.style, 'line-height', 'inherit');
            setImp(el.style, 'overflow', 'hidden');
            setImp(el.style, 'text-overflow', 'ellipsis');
        });
    });
}
//  tbody  Flag / Reason / Secondary
function wrapExceptionTbodyCells(table: HTMLTableElement) {
    const thead = table.querySelector('thead');
    const tbody = table.querySelector('tbody');
    if (!thead || !tbody) return;

    // index column (case-insensitive)
    const ths = Array.from(thead.querySelectorAll('th')) as HTMLTableCellElement[];
    const wanted = new Set(['EXCEPTION FLAG', 'EXCEPTION REASON', 'EXCEPTION SECONDARY REASON']);

    const targetIdx: number[] = [];
    ths.forEach((th, i) => {
        const label = (th.textContent || '').trim().toUpperCase();
        if (wanted.has(label)) targetIdx.push(i);
    });
    if (!targetIdx.length) return;

    // td in selected (tbody)
    const rows = Array.from(tbody.querySelectorAll('tr'));
    rows.forEach((tr) => {
        const tds = Array.from(tr.children) as HTMLTableCellElement[];
        targetIdx.forEach((idx) => {
            const td = tds[idx];
            if (!td) return;

            td.style.setProperty('white-space', 'normal', 'important');
            td.style.setProperty('word-break', 'break-word', 'important');
            td.style.setProperty('overflow-wrap', 'anywhere', 'important');
            td.style.setProperty('overflow', 'visible', 'important');
            td.style.setProperty('text-overflow', 'clip', 'important');
            td.style.setProperty('line-height', '1.35', 'important');

            Array.from(td.children).forEach((ch) => {
                const s = (ch as HTMLElement).style;
                s.setProperty('white-space', 'inherit', 'important');
                s.setProperty('word-break', 'inherit', 'important');
                s.setProperty('overflow-wrap', 'inherit', 'important');
                s.setProperty('overflow', 'inherit', 'important');
                s.setProperty('line-height', 'inherit', 'important');
                s.setProperty('max-width', '100%', 'important');
            });
        });
    });
}




const styles = {
    menuItemStyle: { display: 'flex', alignItems: 'center', zIndex: 4, width: '240px', margin: '1px', height: '40px' } as const,
    menuItemIconStyle: { paddingRight: '10px', paddingLeft: '10px', display: 'flex', alignItems: 'center' } as const,
    menuItemTextStyle: { color: 'white' } as const,
    infoTipStyle: { margin: '3px' } as const,
};

const InfoCardContent = ({ stdAddress }: { stdAddress: string }) => (
    <div id="menu-list-quote" style={{ maxWidth: 'fit-content' }}>
        <div style={styles.menuItemStyle}>
      <span style={styles.menuItemIconStyle}>
        <img alt="home icon" src={`${BaseUrl.widgetImage}/home-Icon.svg`} />
      </span>
            <div style={styles.menuItemTextStyle}>
                <span>{stdAddress}</span>
            </div>
        </div>
    </div>
);

interface ExceptionFlagTableProps {
    originalWorkOrderCountData: WorkOrderExceptionCountAPIResult[] | undefined;
    mappedWorkOrderCountData: { label: string; value: string; count: number }[] | undefined;
    activeTablePage: number;
    onPageClick: (e: number) => void;
    workOrderFilters: ExceptionFlagFilters;
    chartSelectedValue: EXCEPTION_FLAG;
    recordsPerPage: number;
    userIds: number[];
    displayType: string;
    labelIndex: number;
    exceptionFlags: { name: string; description: string }[] | undefined;
    flagTree: any[];
}

export function ExceptionFlagTable(props: Readonly<ExceptionFlagTableProps>): JSX.Element | null {
    const {
        originalWorkOrderCountData,
        mappedWorkOrderCountData,
        activeTablePage,
        onPageClick,
        workOrderFilters,
        chartSelectedValue,
        recordsPerPage,
        userIds,
        displayType,
        labelIndex,
        exceptionFlags,
        flagTree,
    } = props;

    const [mappedWorkOrderData, setMappedWorkOrderData] = useState<TableRecords[]>([]);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [wrapperWidth, setWrapperWidth] = useState(0);
    const hostRef = useRef<HTMLDivElement | null>(null);

    const tableHeaders: TableHeaders[] = [
        { displayName: 'Document #', value: 'number', sortable: false, ascending: false },
        { displayName: 'Document Type', value: 'workOrderTypeId', sortable: false, ascending: false },
        { displayName: 'Client', value: 'client', sortable: false, ascending: false },
        { displayName: 'Contractor Company', value: 'contractor', sortable: false, ascending: false },
        { displayName: 'Status', value: 'statusId', sortable: false, ascending: false },
        { displayName: 'Exception Flag', value: 'issueId', sortable: false, ascending: false },
        { displayName: 'EXCEPTION REASON', value: 'exceptionReason', sortable: false, ascending: true },
        { displayName: 'EXCEPTION SECONDARY REASON', value: 'exceptionSecondaryReason', sortable: false, ascending: true },
        { displayName: 'Days Since Flagged', value: 'issueAgeDays', sortable: false, ascending: false },
    ];
    const totalNumberOfRecords = useMemo(() => {
        if (displayType === 'Pie Chart') {
            const filtered = mappedWorkOrderCountData?.filter((obj) => obj.value === chartSelectedValue);
            return filtered ? filtered[0]?.count || 0 : 0;
        }
        if (displayType === 'Bar Graph') {
            return (
                originalWorkOrderCountData?.[labelIndex]?.counts?.find((r) => r.label === chartSelectedValue)?.count || 0
            );
        }
        return 0;
    }, [chartSelectedValue, displayType, labelIndex, mappedWorkOrderCountData, originalWorkOrderCountData]);

    const listArgs = useMemo(() => {
        const selectedFlags =
            (workOrderFilters as any).exceptionFlags?.map((f: any) => f?.name ?? f) ?? [];

        const reasonIds =
            workOrderFilters.exceptionReasonIds ??
            ((workOrderFilters as any).exceptionReasons?.map((r: any) => r.value ?? r) ?? []);

        const secondaryIds =
            workOrderFilters.exceptionSecondaryReasonIds ??
            ((workOrderFilters as any).exceptionSecondaryReasons?.map((s: any) => s.value ?? s) ?? []);

        const idx = buildFlagIndex(flagTree);
        const reasonByFlag: Record<string, string[]> = {};
        const secondaryByFlag: Record<string, string[]> = {};

        for (const f of selectedFlags) {
            const node = idx.get(String(f));
            if (!node) continue;
            reasonByFlag[f] = reasonIds.filter((id) => node.reasons.has(String(id)));
            secondaryByFlag[f] = secondaryIds.filter((id) => node.secondary.has(String(id)));
        }

        const multiFlags = selectedFlags.length > 1;
        let sendGlobalReasons = !multiFlags;
        let sendGlobalSecondary = !multiFlags;

        if (!multiFlags && selectedFlags.length === 1) {
            const f = selectedFlags[0];
            if (reasonIds.length && (reasonByFlag[f]?.length ?? 0) !== reasonIds.length) sendGlobalReasons = false;
            if (secondaryIds.length && (secondaryByFlag[f]?.length ?? 0) !== secondaryIds.length) sendGlobalSecondary = false;
        }

        const base = {
            ...workOrderFilters,
            exceptionFlags: (reasonIds.length || secondaryIds.length) ? selectedFlags : [chartSelectedValue],
            userIds: displayType === 'Bar Graph' ? userIds : workOrderFilters.userIds,
            noOfDocumentsToShow: recordsPerPage,
        };

        return {
            ...base,
            exceptionReasonIds: sendGlobalReasons ? reasonIds : undefined,
            exceptionSecondaryReasonIds: sendGlobalSecondary ? secondaryIds : undefined,
            exceptionReasonIdsByFlag: reasonByFlag,
            exceptionSecondaryReasonIdsByFlag: secondaryByFlag,
        };
    }, [workOrderFilters, chartSelectedValue, displayType, userIds, recordsPerPage, flagTree]);

    const response = useGetFilteredWorkOrderExceptionsQuery(listArgs, { refetchOnMountOrArgChange: true });
    const GetFilteredWorkOrdersData = response?.data?.result;

    const isReady = useMemo(() => !!GetFilteredWorkOrdersData && !!flagTree, [GetFilteredWorkOrdersData, flagTree]);

    // measure container width to ensure correct DataTable layout
    useLayoutEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;
        setWrapperWidth(Math.round(el.getBoundingClientRect().width));
        const ro = new ResizeObserver(([entry]) => {
            setWrapperWidth(Math.round(entry.contentRect.width));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // transform data into table rows
    useLayoutEffect(() => {
        if (!GetFilteredWorkOrdersData) return;
        const keyOf = (v: unknown) => String(v ?? '').trim();
        const aliasOf = (s: string) => s.toUpperCase().replace(/[\s-]+/g, '_');
        const reasonMap = new Map<string, string>();
        const secondaryMap = new Map<string, string>();

        (flagTree ?? []).forEach((f: any) => {
            (f.reasons ?? []).forEach((r: any) => {
                const disp = keyOf(r.display);
                reasonMap.set(keyOf(r.id), disp);
                reasonMap.set(aliasOf(disp), disp);
                (r.secondaryReasons ?? []).forEach((s: any) => {
                    const sDisp = keyOf(s.display);
                    secondaryMap.set(keyOf(s.id), sDisp);
                    secondaryMap.set(aliasOf(sDisp), sDisp);
                });
            });
        });

        const toReasonText = (ids?: (string | number | null)[] | null) =>
            (ids ?? [])
                .filter((x): x is string | number => x !== null && x !== undefined)
                .map((id) => reasonMap.get(keyOf(id)) ?? snakeCaseToTitleCase(keyOf(id)))
                .join(', ') || '-';

        const toSecondaryText = (ids?: (string | number | null)[] | null) =>
            (ids ?? [])
                .filter((x): x is string | number => x !== null && x !== undefined)
                .map((id) => secondaryMap.get(keyOf(id)) ?? snakeCaseToTitleCase(keyOf(id)))
                .join(', ') || '-';

        const toArr = (v?: unknown) => {
            if (!v) return [];
            if (Array.isArray(v)) return v;
            if (typeof v === 'string') {
                const t = v.trim();
                if (t.startsWith('[') && t.endsWith(']')) {
                    try { return JSON.parse(t); } catch { /* ignore */ }
                }
                return [v];
            }
            return [];
        };

        setMappedWorkOrderData(
            GetFilteredWorkOrdersData.map((record, index) => {
                const numberLink = `/#/ops/work-orders/${record.workOrder.id}/details`;
                const clientLink = `/#/ops/clients/${record.workOrder.client.id}/details`;
                const contractorLink = `/#/ops/contractors/${record.workOrder.contractor?.id}/details`;
                const reasonIds = toArr(
                    (record as any).exceptionReasonIds ??
                    (record as any).issueReasonIds ??
                    (record as any).exceptionReasonId ??
                    (record as any).issueReasonId ??
                    (record as any).exceptionReason
                );

                const secondaryIds = toArr(
                    (record as any).exceptionSecondaryReasonIds ??
                    (record as any).issueSecondaryReasonIds ??
                    (record as any).exceptionSecondaryReasonId ??
                    (record as any).issueSecondaryReasonId ??
                    (record as any).exceptionSecondaryReason
                );
                const workOrderTypeDisplay = snakeCaseToTitleCase(record.workOrder.workOrderTypeId);
                const flagDisplay =
                    exceptionFlags?.find((f) => f?.name === record?.issueId)?.description
                    ?? snakeCaseToTitleCase(record.issueId);

                return {
                    number: (
                        <a data-test-id={`EFTableIdLink${index}`} href={numberLink} target="_blank" rel="noopener noreferrer">
                            {record.workOrder.number}
                        </a>
                    ),
                    workOrderTypeId: workOrderTypeDisplay ?? '-',
                    client: (
                        <div>
                            <img
                                id={`exc-flag-info-${record.workOrder.id}-${index}`}
                                alt="info icon"
                                width={12}
                                height={12}
                                style={styles.infoTipStyle}
                                src={`${BaseUrl.widgetImage}/info-icon.svg`}
                            />
                            <Tooltip
                                id={`exc-flag-info-tooltip-${record.workOrder.id}-${index}`}
                                anchorSelect={`#exc-flag-info-${record.workOrder.id}-${index}`}
                                openOnClick
                                place="bottom-start"
                                opacity={1}
                                style={{ ...styles.infoTipStyle, backgroundColor: '#0e2248', zIndex: 5, borderRadius: 6, maxWidth: 'fit-content' }}
                                disableStyleInjection={'core'}
                            >
                                <InfoCardContent stdAddress={record.workOrder.client.address.stdAddress} />
                            </Tooltip>
                            <a data-test-id={`EFTableClientLink${index}`} href={clientLink} target="_blank" rel="noopener noreferrer">
                                {record.workOrder.client.name}
                            </a>
                        </div>
                    ),
                    contractor: record.workOrder.contractor?.name ? (
                        <a data-test-id={`EFTableContractorLink${index}`} href={contractorLink} target="_blank" rel="noopener noreferrer">
                            {record.workOrder.contractor?.name}
                        </a>
                    ) : 'NCF',
                    statusId: snakeCaseToTitleCase(record.workOrder.statusId) ?? '-',
                    issueId: flagDisplay ?? '-',
                    exceptionReason: toReasonText(reasonIds),
                    exceptionSecondaryReason: toSecondaryText(secondaryIds),
                    issueAgeDays: record.issueAgeDays ?? '-',
                };
            })
        );
    }, [GetFilteredWorkOrdersData, exceptionFlags, flagTree]);

    const dataTableKey = useMemo(
        () => `ef-${chartSelectedValue}-${activeTablePage}-${recordsPerPage}-${mappedWorkOrderData.length}-${wrapperWidth}`,
        [chartSelectedValue, activeTablePage, recordsPerPage, mappedWorkOrderData.length, wrapperWidth]
    );

    // Wait for the container to stabilize, then remove Bootstrap classes and apply inline styles
    useLayoutEffect(() => {
        const root = hostRef.current;
        if (!root) return;

        const apply = () => {
            const table = root.querySelector('table');
            if (!table) return;
            table.classList.remove('table', 'table-hover');
            table.classList.add('motili-data-table');
            normalizeTbody(root);
            forceHorizontalOnlyScroll(root);
            wrapExceptionTbodyCells(table);
        };

        let cancelled = false;
        (async () => { await waitForStable(root); if (!cancelled) apply(); })();

        const mo = new MutationObserver(() => {
            apply();
        });
        mo.observe(root, { childList: true, subtree: true });

        return () => { cancelled = true; mo.disconnect(); };
    }, [dataTableKey]);



    return isReady && mappedWorkOrderData.length > 0 ? (
        <div className="ef-scope" ref={wrapperRef} style={{ width: '100%' }}>
            <div className="ef-table-scroll">
                <div className="ef-host" ref={hostRef}>
                    <DataTable
                        key={dataTableKey}
                        headerBackground={'#0E2248'}
                        headerTextColor={'#FFFFFF'}
                        data-test-id={'EFWidgetClosedTable'}
                        totalRecordCount={totalNumberOfRecords || 0}
                        records={mappedWorkOrderData}
                        tableHeaders={tableHeaders}
                        showPagination={false}
                        activePage={activeTablePage}
                        recordsPerPage={recordsPerPage}
                        onPageClick={(pageNumber) => onPageClick(pageNumber)}
                        onListItemClick={() => {}}
                        onTableHeaderItemClick={() => {}}
                    />
                </div>
            </div>
            <div style={{  display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%' }}>
                <ShowingInline
                    total={totalNumberOfRecords || 0}
                    page={activeTablePage}
                    perPage={recordsPerPage}
                />
                <div style={{ flex: 1 }} />
                <PaginationInline
                    total={totalNumberOfRecords || 0}
                    page={activeTablePage}
                    perPage={recordsPerPage}
                    onChange={onPageClick}
                />
            </div>
        </div>
    ) : null;
}
